import { calculateKineticEnergy } from './projectiles.js';
import { degToRad, EPSILON } from './geometry.js';
/**
 * Standard Kinetic Penetration Model (AP / APFSDS / Target Practice).
 *
 * Implements a calibrated gameplay power-law penetration equation:
 *   P(v) = P_nominal * (v / v_muzzle)^1.4
 *
 * Effective armor resistance:
 *   R_eff = t_effective * material.rhaEquivalentKinetic
 *
 * Shell normalization:
 *   theta_norm = max(0, theta - normalizationAngleDeg)
 *
 * Clearly labeled: Calibrated gameplay simulation model.
 */
export class StandardKineticPenetrationModel {
    name = 'StandardKineticPenetrationModel';
    description = 'Gameplay-calibrated kinetic energy penetration model based on De Marre velocity scaling and shell normalization.';
    evaluate(input) {
        const { projectile, impactVelocityMs, incidenceAngleDeg, material, nominalThicknessMm, isSpaced, } = input;
        // 1. Apply shell normalization effect (blunt shell tip bites into armor plate, reducing slope)
        const normAngleDeg = projectile.normalizationAngleDeg || 0;
        const effectiveAngleDeg = Math.max(0, incidenceAngleDeg - normAngleDeg);
        const cosAngle = Math.max(Math.cos(degToRad(effectiveAngleDeg)), EPSILON);
        // 2. Line of sight thickness after normalization
        const normalizedLOSMm = nominalThicknessMm / cosAngle;
        // 3. Equivalent RHA thickness in mm
        const equivalentRhaMm = normalizedLOSMm * material.rhaEquivalentKinetic;
        // 4. Kinetic penetration capacity at impact velocity
        const velocityRatio = Math.max(0, impactVelocityMs / projectile.muzzleVelocityMs);
        // De Marre scaling exponent (~1.4 for kinetic penetrators)
        const currentPenetrationMm = projectile.nominalPenetrationMm * Math.pow(velocityRatio, 1.4);
        const initialEnergy = calculateKineticEnergy(projectile.massKg, impactVelocityMs);
        // 5. Penetration decision
        if (currentPenetrationMm >= equivalentRhaMm) {
            // Penetrated!
            const resistanceRatio = Math.min(1.0, equivalentRhaMm / Math.max(currentPenetrationMm, EPSILON));
            // Energy remaining after displacing armor plate
            const residualEnergyFraction = Math.max(0.05, 1.0 - resistanceRatio);
            const residualEnergy = initialEnergy * residualEnergyFraction;
            const residualVelocityMs = Math.sqrt((2 * residualEnergy) / projectile.massKg);
            // Generate spall fragments inside behind armor
            const spallCount = Math.round(5 * material.spallMultiplier);
            const spallParticles = [];
            const baseSpallDmg = Math.round(15 * material.spallMultiplier * residualEnergyFraction);
            for (let i = 0; i < spallCount; i++) {
                const spreadX = (Math.random() - 0.5) * 0.4;
                const spreadY = (Math.random() - 0.5) * 0.4;
                spallParticles.push({
                    direction: { x: spreadX, y: spreadY, z: -1 },
                    velocityMs: residualVelocityMs * (0.3 + Math.random() * 0.4),
                    damage: Math.max(1, Math.round(baseSpallDmg / spallCount)),
                });
            }
            const directDamage = Math.round(40 * (residualEnergy / initialEnergy) + 20);
            return {
                penetrated: true,
                residualVelocityMs: Math.round(residualVelocityMs * 10) / 10,
                residualEnergyJoules: Math.round(residualEnergy),
                penetrationDepthMm: Math.round(normalizedLOSMm),
                damage: directDamage,
                spallParticles,
                confidence: 0.92,
                reason: `Kinetic penetrator defeated armor: ${Math.round(currentPenetrationMm)}mm penetration vs ${Math.round(equivalentRhaMm)}mm effective RHA.`,
            };
        }
        else {
            // Stopped / Incomplete penetration
            const ratio = currentPenetrationMm / Math.max(equivalentRhaMm, EPSILON);
            const penetrationDepthMm = Math.round(normalizedLOSMm * ratio);
            return {
                penetrated: false,
                residualVelocityMs: 0,
                residualEnergyJoules: 0,
                penetrationDepthMm,
                damage: Math.round(ratio * 5), // Minor blunt trauma on close partial penetrations
                spallParticles: [],
                confidence: 0.94,
                reason: `Armor stopped penetrator: ${Math.round(equivalentRhaMm)}mm effective RHA resisted ${Math.round(currentPenetrationMm)}mm penetration potential.`,
            };
        }
    }
}
/**
 * Chemical Energy Penetration Model (HEAT / HE).
 *
 * Munroe shaped-charge jet dynamics:
 * - Penetration is primarily independent of vehicle velocity (chemical explosion).
 * - Spaced armor / air gaps disrupt and dissipate the copper jet stream.
 * - HE produces blast overpressure and surface fracturing.
 */
export class ChemicalEnergyPenetrationModel {
    name = 'ChemicalEnergyPenetrationModel';
    description = 'Gameplay-calibrated chemical energy model for shaped charge (HEAT) jets and high explosive blast overpressure.';
    evaluate(input) {
        const { projectile, incidenceAngleDeg, material, nominalThicknessMm, isSpaced, } = input;
        const cosAngle = Math.max(Math.cos(degToRad(incidenceAngleDeg)), EPSILON);
        const lineOfSightMm = nominalThicknessMm / cosAngle;
        const equivalentRhaMm = lineOfSightMm * material.rhaEquivalentChemical;
        if (projectile.projectileType === 'HEAT') {
            // Shaped charge jet penetration
            let jetPenetrationMm = projectile.nominalPenetrationMm;
            // Spaced armor air gap causes jet tip dispersal
            if (isSpaced) {
                jetPenetrationMm *= 0.65; // 35% reduction from air gap disturbance
            }
            if (jetPenetrationMm >= equivalentRhaMm) {
                // Jet penetrated into interior compartment
                const excessMm = jetPenetrationMm - equivalentRhaMm;
                const damage = Math.round(55 + (excessMm / jetPenetrationMm) * 35);
                return {
                    penetrated: true,
                    residualVelocityMs: 400, // Molten copper jet tip remnant
                    residualEnergyJoules: 150000,
                    penetrationDepthMm: Math.round(lineOfSightMm),
                    damage,
                    spallParticles: [
                        { direction: { x: 0, y: 0, z: -1 }, velocityMs: 500, damage: 25 },
                    ],
                    confidence: 0.88,
                    reason: `HEAT shaped-charge jet penetrated: ${Math.round(jetPenetrationMm)}mm jet vs ${Math.round(equivalentRhaMm)}mm chemical RHA.`,
                };
            }
            else {
                return {
                    penetrated: false,
                    residualVelocityMs: 0,
                    residualEnergyJoules: 0,
                    penetrationDepthMm: Math.round(lineOfSightMm * (jetPenetrationMm / equivalentRhaMm)),
                    damage: 10, // Minor blast scorch
                    spallParticles: [],
                    confidence: 0.9,
                    reason: `Composite / Spaced armor dispersed HEAT jet: ${Math.round(equivalentRhaMm)}mm resisted ${Math.round(jetPenetrationMm)}mm jet.`,
                };
            }
        }
        else {
            // HE (High-Explosive Blast / Spall)
            const blastPenetrationMm = projectile.nominalPenetrationMm;
            if (blastPenetrationMm >= equivalentRhaMm) {
                return {
                    penetrated: true,
                    residualVelocityMs: 0,
                    residualEnergyJoules: 0,
                    penetrationDepthMm: Math.round(lineOfSightMm),
                    damage: 80, // Catastrophic blast breach
                    spallParticles: [],
                    confidence: 0.85,
                    reason: `HE blast overpressure ruptured thin armor plate (${nominalThicknessMm}mm).`,
                };
            }
            else {
                // Hull spall from massive shockwave even without penetration
                const shockFactor = Math.max(0, 1 - equivalentRhaMm / 120);
                const splashDamage = Math.round((projectile.explosiveMassKg || 2) * 12 * shockFactor);
                return {
                    penetrated: false,
                    residualVelocityMs: 0,
                    residualEnergyJoules: 0,
                    penetrationDepthMm: Math.round(blastPenetrationMm),
                    damage: splashDamage,
                    spallParticles: [],
                    confidence: 0.9,
                    reason: `HE blast detonated externally against ${nominalThicknessMm}mm plate. Shock damage: ${splashDamage} HP.`,
                };
            }
        }
    }
}
//# sourceMappingURL=penetration.js.map